import {useCallback, useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

// Note: use this function while component adding same url into browser history stacks
export function useSafeNavigateBack(
  fallback: string,
  options?: {forceFallback?: boolean},
) {
  const navigate = useNavigate();
  const location = useLocation();

  const pendingBackRef = useRef<{
    url: string;
    fallback: string;
  } | null>(null);

  const currentUrl = location.pathname + location.search + location.hash;

  useEffect(() => {
    const pending = pendingBackRef.current;

    if (!pending) return;

    // Stop when we reach a different URL.
    if (currentUrl !== pending.url) {
      pendingBackRef.current = null;
      return;
    }

    // Same URL, but a different history entry: skip it.
    const idx = (window.history.state as {idx?: number} | null)?.idx;

    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      pendingBackRef.current = null;
      navigate(pending.fallback, {replace: true});
    }
  }, [currentUrl, location.key, navigate]);

  return useCallback(() => {
    if (options?.forceFallback) {
      pendingBackRef.current = null;
      navigate(fallback, {replace: true});
      return;
    }

    const idx = (window.history.state as {idx?: number} | null)?.idx;

    if (typeof idx !== 'number' || idx <= 0) {
      pendingBackRef.current = null;
      navigate(fallback, {replace: true});
      return;
    }

    pendingBackRef.current = {
      url: currentUrl,
      fallback,
    };

    navigate(-1);
  }, [currentUrl, fallback, navigate, options?.forceFallback]);
}
